import React, { useState } from 'react';
import { Lock } from 'lucide-react';
import eventService from '../services/eventService';

/**
 * Feedback widget ("Wie gefällt dir Radsport Events?") — 5-diamond rating, live sentiment label,
 * optional comment, optional contact e-mail and a thank-you state.
 * Anonymous unless the user provides an e-mail: { rating, comment, email, platform } is sent.
 *
 * `variant` controls copy + the platform tag:
 *  - 'web': desktop events sidebar ("…die Seite…", platform 'web'), with hover-preview.
 *  - 'app': mobile / profile placement ("…die App…", platform 'app').
 */
type Props = {
  variant?: 'web' | 'app';
  className?: string;
  style?: React.CSSProperties;
};

export const SENTIMENTS = ['Tippe zum Bewerten', 'Gar nicht', 'Geht so', 'Okay', 'Richtig gut', 'Super!'];
export const SENTIMENT_COLORS = ['#C4BFBF', '#A82F5A', '#E0A100', '#6A6A6A', '#922C57', '#1F8A5B'];

const FONT_HEAD = "'Archivo',sans-serif";
const FONT_BODY = "'Hanken Grotesk',sans-serif";
const FONT_MONO = "'Space Mono',monospace";

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Ratings up to this value require a comment — we want to know what went wrong (mirrors the backend). */
export const COMMENT_REQUIRED_MAX_RATING = 3;

const FeedbackWidget: React.FC<Props> = ({ variant = 'web', className, style }) => {
  const isApp = variant === 'app';
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState('');
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const active = hover || rating;
  const size = isApp ? 50 : 44;
  const emailInvalid = email.trim() !== '' && !EMAIL_PATTERN.test(email.trim());
  const commentRequired = rating > 0 && rating <= COMMENT_REQUIRED_MAX_RATING;
  const commentMissing = commentRequired && text.trim() === '';
  const disabled = submitting || emailInvalid || commentMissing;

  const reset = () => {
    setRating(0);
    setHover(0);
    setText('');
    setEmail('');
    setSent(false);
  };

  const submit = async () => {
    if (rating < 1 || disabled) return;
    setSubmitting(true);
    try {
      await eventService.submitFeedback({
        rating,
        comment: text.trim() || undefined,
        email: email.trim() || undefined,
        platform: isApp ? 'app' : 'web',
      });
      setSent(true);
    } catch {
      // Non-blocking: still show the thank-you state so anonymous users aren't stuck.
      setSent(true);
    } finally {
      setSubmitting(false);
    }
  };

  const card: React.CSSProperties = {
    position: 'relative',
    overflow: 'hidden',
    background: '#fff',
    border: '1px solid #E5E1E1',
    borderRadius: 16,
    padding: isApp ? 16 : 20,
    ...style,
  };

  const decoration = (
    <div
      aria-hidden
      style={{
        position: 'absolute', right: -34, top: -34, width: 120, height: 120,
        background: '#922C57', opacity: 0.06, borderRadius: 24, transform: 'rotate(45deg)',
        pointerEvents: 'none',
      }}
    />
  );

  if (sent) {
    return (
      <div className={className} style={card}>
        {decoration}
        <div style={{
          position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center',
          textAlign: 'center', padding: '20px 4px',
        }}>
          <div style={{
            width: 53, height: 53, background: '#922C57', borderRadius: 15, transform: 'rotate(45deg)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ transform: 'rotate(-45deg)', color: '#fff', fontSize: 27, fontWeight: 800, lineHeight: 1 }}>✓</span>
          </div>
          <div style={{ fontFamily: FONT_HEAD, fontWeight: 800, fontSize: 19, color: '#2C2C2C', marginTop: 18 }}>
            Danke für dein Feedback!
          </div>
          <div style={{ fontFamily: FONT_BODY, fontSize: 13, color: '#6A6A6A', lineHeight: 1.5, marginTop: 8 }}>
            Wir lesen jede Rückmeldung und verbessern {isApp ? 'die App' : 'Radsport Events'} Schritt für Schritt.
          </div>
          <span
            onClick={reset}
            style={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: 13, color: '#922C57', marginTop: 16, cursor: 'pointer' }}
          >
            Weiteres Feedback geben
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={className} style={card}>
      {decoration}
      <div style={{ position: 'relative' }}>
        <span style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.16em', color: '#922C57' }}>
          {'// DEINE MEINUNG'}
        </span>
        <div style={{ fontFamily: FONT_HEAD, fontWeight: 800, fontSize: 18, lineHeight: 1.15, color: '#2C2C2C', marginTop: 8 }}>
          {isApp ? 'Wie gefällt dir die App?' : 'Wie gefällt dir Radsport Events?'}
        </div>
        <div style={{ fontFamily: FONT_BODY, fontSize: 12, color: '#9A9494', marginTop: 4 }}>
          {isApp ? 'Hilf uns, die App besser zu machen.' : 'Hilf uns, die Seite besser zu machen.'}
        </div>

        {/* Rating row */}
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          {[1, 2, 3, 4, 5].map((i) => {
            const filled = i <= active;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setRating(i)}
                onMouseEnter={() => !isApp && setHover(i)}
                onMouseLeave={() => !isApp && setHover(0)}
                style={{
                  flex: 1, height: size, padding: 0, borderRadius: 11, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: filled ? 'rgba(146,44,87,0.08)' : '#F5F3F3',
                  border: `1px solid ${filled ? '#922C57' : '#E5E1E1'}`,
                  transition: 'background 120ms, border-color 120ms',
                }}
                aria-label={`Bewertung ${i}`}
              >
                <span style={{
                  width: 16, height: 16, borderRadius: 4, transform: 'rotate(45deg)',
                  background: filled ? '#922C57' : '#D9D3D3',
                }} />
              </button>
            );
          })}
        </div>

        {/* Sentiment */}
        <div style={{
          height: 18, marginTop: 10, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600,
          color: SENTIMENT_COLORS[active],
        }}>
          {SENTIMENTS[active]}
        </div>

        {/* Comment + send (only after a rating is picked) */}
        {rating > 0 && (
          <>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={commentRequired
                ? 'Was hat dir nicht gefallen? (erforderlich)'
                : 'Was können wir besser machen? (optional)'}
              maxLength={2000}
              style={{
                width: '100%', height: 68, resize: 'none', marginTop: 10, boxSizing: 'border-box',
                background: '#F5F3F3', border: `1px solid ${commentMissing ? '#A82F5A' : '#E5E1E1'}`,
                borderRadius: 11, padding: 12,
                fontFamily: FONT_BODY, fontSize: 13, color: '#2C2C2C', outline: 'none',
              }}
            />
            {commentMissing && (
              <div style={{ fontFamily: FONT_BODY, fontSize: 11, color: '#A82F5A', marginTop: 4 }}>
                Bitte sag uns kurz, was wir besser machen können.
              </div>
            )}
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="E-Mail für Rückfragen (optional)"
              maxLength={255}
              style={{
                width: '100%', marginTop: 8, boxSizing: 'border-box',
                background: '#F5F3F3', border: `1px solid ${emailInvalid ? '#A82F5A' : '#E5E1E1'}`,
                borderRadius: 11, padding: '10px 12px',
                fontFamily: FONT_BODY, fontSize: 13, color: '#2C2C2C', outline: 'none',
              }}
            />
            {emailInvalid && (
              <div style={{ fontFamily: FONT_BODY, fontSize: 11, color: '#A82F5A', marginTop: 4 }}>
                Bitte gib eine gültige E-Mail-Adresse an.
              </div>
            )}
            <button
              type="button"
              onClick={submit}
              disabled={disabled}
              style={{
                width: '100%', marginTop: 10, background: '#2C2C2C', color: '#fff', border: 'none',
                padding: 12, borderRadius: 11, fontFamily: FONT_BODY, fontWeight: 700, fontSize: 14,
                cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1,
              }}
            >
              {submitting ? 'Senden…' : 'Feedback senden'}
            </button>
          </>
        )}

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, marginTop: 12,
          fontFamily: FONT_BODY, fontSize: 12, color: '#6A6A6A',
        }}>
          <Lock size={13} aria-hidden />
          <span>Anonym · E-Mail nur, wenn du magst</span>
        </div>
      </div>
    </div>
  );
};

export default FeedbackWidget;
